import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule, TooltipPosition } from '@angular/material/tooltip';
import { ADPResourceService } from 'digital-core';
import { GoogleAnalyticsService } from 'src/app/core/core';
import { GTMTooltipInteractionType } from 'src/app/model/google-tag-manager/api-status';

@Component({
  selector: 'adp-info-tooltip',
  standalone: true,
  imports: [MatIcon, MatTooltipModule],
  templateUrl: './info-tooltip.component.html',
  styleUrl: './info-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoTooltipComponent {
  private readonly resourceService = inject(ADPResourceService);
  private readonly analyticsService = inject(GoogleAnalyticsService);

  readonly contentKey = input.required<string>();
  readonly ariaLabelKey = input.required<string>();
  readonly analyticsItem = input.required<string>();
  readonly analyticsContext = input.required<string>();
  readonly position = input<TooltipPosition>('above');

  private readonly fallbackAriaLabelKey = 'Global_InfoTooltip_Default_Aria_Label';

  readonly content = computed(() => this.resolveLabel(this.contentKey()));
  readonly ariaLabel = computed(() => this.resolveLabel(this.ariaLabelKey()) || this.resolveLabel(this.fallbackAriaLabelKey));

  private hasFiredHover = false;

  onTriggerClick(event: MouseEvent, tooltip: MatTooltip): void {
    event.stopPropagation();
    this.emit(GTMTooltipInteractionType.Tap);
    tooltip.toggle();
  }

  onTriggerHover(): void {
    if (this.hasFiredHover) {
      return;
    }
    this.hasFiredHover = true;
    this.emit(GTMTooltipInteractionType.Hover);
  }

  private emit(interactionType: GTMTooltipInteractionType): void {
    this.analyticsService.emitTooltipEvent(this.analyticsItem(), interactionType, this.analyticsContext());
  }

  private resolveLabel(key: string): string {
    const label = this.resourceService.getViewLabel(key) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
